import React, { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Panel, DsButton } from "../../ds";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../ui/alert-dialog";
import { GalleryCard, isPastDue } from "./GalleryCard";
import { SOCIAL_STATUSES } from "./QueueTable";
import { MonthSelect, currentDubaiMonth } from "./MonthSelect";
import { ArchiveDialog } from "./ArchiveDialog";

const FilterSelect = ({ value, onChange, options, allLabel, testId, width = "w-[200px]" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`h-8 ${width}`} data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {allLabel && <SelectItem value="all">{allLabel}</SelectItem>}
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const Gallery = ({ brands, onChanged }) => {
  const [slug, setSlug] = useState("all");
  const [status, setStatus] = useState("waiting_visual_approval");
  const [channel, setChannel] = useState("all");
  const [month, setMonth] = useState(currentDubaiMonth);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const project = slug;
  const srvStatus = status === "archived" ? "archived" : undefined;

  const { data, refetch } = useQuery({
    queryKey: ["social-items", "gallery", project, month, srvStatus],
    queryFn: async () => (await api.get("/social/items", {
      params: { brand: project === "all" ? undefined : project, month, status: srvStatus },
    })).data,
    placeholderData: keepPreviousData,
  });
  const all = data?.items || [];
  const groups = all.reduce((m, i) => {
    const k = `${i.brand_key}|${i.group_key}`;
    (m[k] = m[k] || []).push(i);
    return m;
  }, {});
  const siblingsOf = (i) => (groups[`${i.brand_key}|${i.group_key}`] || []).filter((s) => s.id !== i.id && !["published", "archived"].includes(s.status));
  const channels = [...new Set(all.map((i) => i.channel))];
  const items = all.filter((i) => (status === "all" || i.status === status) && (channel === "all" || i.channel === channel));
  const waiting = all.filter((i) => i.status === "waiting_visual_approval");
  const concepts = all.filter((i) => i.status === "waiting_for_video");

  const approveProductionAll = async () => {
    try {
      let approved = 0;
      for (const k of [...new Set(concepts.map((i) => i.brand_key))]) {
        const { data: r } = await api.post(`/social/brands/${encodeURIComponent(k)}/approve-production-all`, null,
          { params: { month: month === "all" ? undefined : month } });
        approved += r.approved;
      }
      toast.success(`Approved ${approved} concept${approved === 1 ? "" : "s"} for production.`);
      refetch(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setProdOpen(false); }
  };
  const archivable = items.filter((i) => !["published", "archived"].includes(i.status));
  const changed = () => { refetch(); onChanged(); };

  const archiveFiltered = async (reason) => {
    try {
      const { data: r } = await api.post("/social/queue/archive", { ids: archivable.map((i) => i.id), reason: reason || undefined });
      toast.success(`Archived ${r.archived} item${r.archived === 1 ? "" : "s"}.`);
      changed();
    } catch (e) { toast.error(formatApiError(e)); } finally { setArchiveOpen(false); }
  };

  const approveAll = async () => {
    try {
      const keys = [...new Set(waiting.map((i) => i.brand_key))];
      let approved = 0, pastDue = 0;
      for (const k of keys) {
        const { data: r } = await api.post(`/social/brands/${encodeURIComponent(k)}/approve-all`, null,
          { params: { month: month === "all" ? undefined : month } });
        approved += r.approved; pastDue += r.past_due;
      }
      const r = { approved, past_due: pastDue };
      if (r.past_due) toast.warning(`Approved ${r.approved}. ${r.past_due} past their time will post at the next run.`);
      else toast.success(`Approved ${r.approved} item${r.approved === 1 ? "" : "s"}.`);
      changed();
    } catch (e) { toast.error(formatApiError(e)); } finally { setConfirmOpen(false); }
  };

  const action = (
    <div className="flex flex-wrap gap-2 justify-end">
      <FilterSelect value={project} onChange={setSlug} allLabel="All brands" options={brands.map((b) => ({ value: b.slug, label: b.name }))} testId="gallery-filter-project" width="w-[190px]" />
      <MonthSelect value={month} onChange={setMonth} brand={project} status={status} allLabel="All months" testId="gallery-filter-month" />
      <FilterSelect value={status} onChange={setStatus} allLabel="All statuses" options={SOCIAL_STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))} testId="gallery-filter-status" />
      <FilterSelect value={channel} onChange={setChannel} allLabel="All channels" options={channels.map((c) => ({ value: c, label: c }))} testId="gallery-filter-channel" width="w-[150px]" />
      <DsButton variant="go" className="h-8" disabled={waiting.length === 0} onClick={() => setConfirmOpen(true)} data-testid="gallery-approve-all-btn">
        Approve all remaining ({waiting.length})
      </DsButton>
      <DsButton variant="go" className="h-8" disabled={concepts.length === 0} onClick={() => setProdOpen(true)} data-testid="gallery-approve-production-all-btn">
        Approve all for production ({concepts.length})
      </DsButton>
      <DsButton variant="no" className="h-8" disabled={archivable.length === 0} onClick={() => setArchiveOpen(true)} data-testid="gallery-archive-filtered-btn">
        Archive filtered items ({archivable.length})
      </DsButton>
    </div>
  );

  return (
    <Panel label={`Visual approval · ${items.length}`} action={action} testId="social-gallery-panel">
      {items.length === 0
        ? <p className="py-10 text-center text-sm text-[color:var(--muted)]" data-testid="gallery-empty">No items match these filters.</p>
        : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 max-h-[600px] overflow-y-auto pr-1" data-testid="gallery-grid">
            {items.map((it) => <GalleryCard key={`${it.id}-${it.updated_at}`} item={it} siblings={siblingsOf(it)} onChanged={changed} />)}
          </div>
        )}
      <AlertDialog open={prodOpen} onOpenChange={setProdOpen}>
        <AlertDialogContent data-testid="approve-production-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve {concepts.length} concept{concepts.length === 1 ? "" : "s"} for production?</AlertDialogTitle>
            <AlertDialogDescription>
              Only concepts waiting for video in the current brand and month filter are approved. The VPS then renders each video and sends it back for visual approval.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="approve-production-all-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={approveProductionAll} data-testid="approve-production-all-confirm">Approve for production</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="approve-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve all {waiting.length} remaining items?</AlertDialogTitle>
            <AlertDialogDescription>
              Only items still waiting for visual approval are approved with their current captions.
              {waiting.some((i) => isPastDue(i.scheduled_at)) && " Some are past their scheduled time and will post at the next run."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="approve-all-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={approveAll} data-testid="approve-all-confirm">Approve all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {archiveOpen && (
        <ArchiveDialog open={archiveOpen} onOpenChange={setArchiveOpen} count={archivable.length} onConfirm={archiveFiltered} testId="archive-filtered-dialog" />
      )}
    </Panel>
  );
};
